import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ColumnFiltersState, OnChangeFn } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { DatePicker } from '@/components/core/date-picker/date-picker';
import { OptionRowsSkeleton, PageHeader } from '@/components/core';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { filterRowsByColumns } from '@/lib/table-query';
import { useInitialPageSize } from '@/hooks/use-server-table';
import { DepartmentFilter } from '@/modules/org-setup/hr-department-manager';
import { useDepartmentScope } from '@/modules/shared/hr-lookups';
import { createTimesheetRollupTableColumns } from '../../components/timesheet-rollup-table/timesheet-rollup-table-columns';
import {
  useGetMbmProjectsLookup,
  useGetUsersTimesheetRollup,
} from '../../hooks/use-timesheet-manager';
import { MbmProjectsResult, ResourceBreakdown } from '../../types/timesheet-manager.types';
import { endOfPreviousMonthDay, orgToday, startOfPreviousMonthDay } from '../../../utils/task-date';

interface PaginationState {
  pageIndex: number;
  pageSize: number;
}

const ALL_VALUE = 'all';

export const TimesheetManagerPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [displayName, setDisplayName] = useState('');
  const [dateRange, setDateRange] = useState(() => {
    const today = orgToday();
    return { from: startOfPreviousMonthDay(today), to: endOfPreviousMonthDay(today) };
  });
  const [departmentId, setDepartmentId] = useState('');
  const [customerId, setCustomerId] = useState(ALL_VALUE);
  const [projectId, setProjectId] = useState(ALL_VALUE);
  const initialPageSize = useInitialPageSize(15);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: initialPageSize,
  });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const { getDepartmentIdCondition } = useDepartmentScope();

  const { data: mbmProjectsData, isLoading: isProjectsLoading } = useGetMbmProjectsLookup();
  const mbmProjects = useMemo(
    () => (mbmProjectsData as { MbmProjects?: MbmProjectsResult })?.MbmProjects?.items ?? [],
    [mbmProjectsData]
  );

  const customers = useMemo(() => {
    const map = new Map<string, string>();
    mbmProjects.forEach((project) => map.set(project.CustomerId, project.CustomerName));
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [mbmProjects]);

  const projects = useMemo(
    () =>
      customerId === ALL_VALUE
        ? mbmProjects
        : mbmProjects.filter((project) => project.CustomerId === customerId),
    [mbmProjects, customerId]
  );

  const { data, isLoading, error } = useGetUsersTimesheetRollup({
    startDate: dateRange.from,
    endDate: dateRange.to,
    departmentId: getDepartmentIdCondition(departmentId),
    customerId: customerId === ALL_VALUE ? undefined : customerId,
    projectId: projectId === ALL_VALUE ? undefined : projectId,
    displayName: displayName || undefined,
  });
  // Every matching entry is loaded at once and rolled up per employee here, so the table sorts,
  // filters and pages the whole list itself.
  const resources = (data as ResourceBreakdown[]) ?? [];

  const resetPage = useCallback(() => setPagination((prev) => ({ ...prev, pageIndex: 0 })), []);

  // New toolbar filters start over from the first page.
  useEffect(() => {
    resetPage();
  }, [displayName, dateRange, departmentId, customerId, projectId, resetPage]);

  const handlePaginationChange = useCallback((next: { pageIndex: number; pageSize: number }) => {
    setPagination(next);
  }, []);

  const handleColumnFiltersChange: OnChangeFn<ColumnFiltersState> = useCallback(
    (updater) => {
      setColumnFilters(updater);
      resetPage();
    },
    [resetPage]
  );

  const columns = createTimesheetRollupTableColumns({ t });
  const totalCount = filterRowsByColumns(resources, columns, columnFilters).length;

  return (
    <div className="flex w-full flex-col gap-4">
      <PageHeader title={t('TIMESHEET_MANAGER')} />

      <div className="grid grid-cols-1 gap-3 min-[360px]:grid-cols-2 md:flex md:flex-wrap md:items-end">
        <div className="space-y-2 min-[360px]:col-span-2 md:col-span-1">
          <Label htmlFor="timesheet-manager-search">{t('EMPLOYEE')}</Label>
          <Input
            id="timesheet-manager-search"
            placeholder={t('TIMESHEET_MANAGER_SEARCH_BY_NAME')}
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className="w-full md:w-48"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="timesheet-manager-start">{t('START_DATE')}</Label>
          <DatePicker
            id="timesheet-manager-start"
            value={dateRange.from}
            onChange={(value) => setDateRange((prev) => ({ ...prev, from: value }))}
            className="w-full md:w-40"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="timesheet-manager-end">{t('END_DATE')}</Label>
          <DatePicker
            id="timesheet-manager-end"
            value={dateRange.to}
            onChange={(value) => setDateRange((prev) => ({ ...prev, to: value }))}
            className="w-full md:w-40"
          />
        </div>
        <div className="space-y-2">
          <Label>{t('DEPARTMENT')}</Label>
          <DepartmentFilter
            value={departmentId}
            onChange={setDepartmentId}
            selectClassName="w-full md:w-44"
          />
        </div>
        <div className="space-y-2">
          <Label>{t('TIMESHEET_MANAGER_CUSTOMER')}</Label>
          <Select
            value={customerId}
            onValueChange={(value) => {
              setCustomerId(value);
              setProjectId(ALL_VALUE);
            }}
          >
            <SelectTrigger className="w-full md:w-44">
              <SelectValue placeholder={t('TIMESHEET_MANAGER_CUSTOMER')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_VALUE}>{t('TIMESHEET_MANAGER_ALL_CUSTOMERS')}</SelectItem>
              {isProjectsLoading && <OptionRowsSkeleton rows={3} />}
              {customers.map((customer) => (
                <SelectItem key={customer.id} value={customer.id}>
                  {customer.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2 min-[360px]:col-span-2 md:col-span-1">
          <Label>{t('TIMESHEET_MANAGER_PROJECT')}</Label>
          <Select value={projectId} onValueChange={setProjectId}>
            <SelectTrigger className="w-full md:w-44">
              <SelectValue placeholder={t('TIMESHEET_MANAGER_PROJECT')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_VALUE}>{t('TIMESHEET_MANAGER_ALL_PROJECTS')}</SelectItem>
              {isProjectsLoading && <OptionRowsSkeleton rows={3} />}
              {projects.map((project) => (
                <SelectItem key={project.ItemId} value={project.ProjectId}>
                  {project.ProjectName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <AdvanceDataTable
        data={resources}
        columns={columns}
        onRowClick={(resource) => navigate(`/hr/timesheet-manager/${resource.UserId}`)}
        isLoading={isLoading}
        error={error instanceof Error ? error : null}
        pagination={{
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
          totalCount,
        }}
        columnFilters={columnFilters}
        onColumnFiltersChange={handleColumnFiltersChange}
        isExpandRowContent={false}
        onPaginationChange={handlePaginationChange}
        mobileView="cards"
      />
    </div>
  );
};
